import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppDataProvider } from './contexts/AppDataContext';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import { FeedProvider } from './contexts/FeedContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { TutorialProvider } from './contexts/TutorialContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import performanceService from './services/performanceService';
import analyticsService from './services/analyticsService';
import GoogleAnalytics from './components/GoogleAnalytics';
import TutorialManager from './components/tutorials/TutorialManager';
import ChatWrapper from './components/chat/ChatWrapper';

// Layout Components
import NavBar from './components/layout/NavBar';
import Sidebar from './components/layout/Sidebar';

// Pages
import HomePage from './pages/HomePage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ProjectForm from './components/projects/ProjectForm';
import ProjectCreationWithLimits from './components/projects/ProjectCreationWithLimits';
import LoginPage from './pages/auth/LoginPage';
import SignUpPage from './pages/auth/SignUpPage';
import CallbackPage from './pages/auth/CallbackPage';
import NotFoundPage from './pages/NotFoundPage';
import featureFlags from './config/featureFlags';

// Lazy loaded pages for code splitting
const FeaturesPage = React.lazy(() => import('./pages/FeaturesPage'));
const FeatureDetailPage = React.lazy(() => import('./pages/FeatureDetailPage'));
const PublicFeaturePage = React.lazy(() => import('./pages/PublicFeaturePage'));
const FeatureForm = React.lazy(() => import('./components/features/FeatureForm'));
const Workboard = React.lazy(() => import('./components/features/Workboard'));
const ReleasePlanner = React.lazy(() => import('./components/features/ReleasePlanner'));
const FeedPage = React.lazy(() => import('./pages/FeedPage'));
const SurveysPage = React.lazy(() => import('./pages/SurveysPage'));
const SurveyTemplatesPage = React.lazy(() => import('./pages/SurveyTemplatesPage'));
const TemplateEditorPage = React.lazy(() => import('./pages/TemplateEditorPage'));
const TemplatePreviewPage = React.lazy(() => import('./pages/TemplatePreviewPage'));
const DeploySurveyPage = React.lazy(() => import('./pages/DeploySurveyPage'));
const CustomerSurveyPage = React.lazy(() => import('./pages/CustomerSurveyPage'));
const SurveyAnalyticsPage = React.lazy(() => import('./pages/SurveyAnalyticsPage'));
const CreateInstantSurveyPage = React.lazy(() => import('./pages/CreateInstantSurveyPage'));
const EditSurveyPage = React.lazy(() => import('./pages/EditSurveyPage'));
const SurveySharePage = React.lazy(() => import('./pages/SurveySharePage'));
const DeployedSurveyResponsesPage = React.lazy(() => import('./pages/DeployedSurveyResponsesPage'));
const PublicRoadmapPage = React.lazy(() => import('./pages/PublicRoadmapPage'));
const PrivateRoadmapPage = React.lazy(() => import('./pages/PrivateRoadmapPage'));
const OrganizationSettingsPage = React.lazy(() => import('./pages/OrganizationSettingsPage'));
const AccountPage = React.lazy(() => import('./pages/AccountPage'));
const MonitoringPage = React.lazy(() => import('./pages/MonitoringPage'));
const InviteAcceptPage = React.lazy(() => import('./pages/InviteAcceptPage'));
const ProjectMembersPage = React.lazy(() => import('./pages/ProjectMembersPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const PlanManagementPage = React.lazy(() => import('./pages/PlanManagementPage'));
const BillingSettings = React.lazy(() => import('./pages/BillingSettings'));
const CheckoutSummary = React.lazy(() => import('./pages/CheckoutSummary'));
const SocialMediaSettings = React.lazy(() => import('./pages/SocialMediaSettings'));
const AtlassianSettingsPage = React.lazy(() => import('./pages/AtlassianSettingsPage'));
const PostComposer = React.lazy(() => import('./pages/PostComposer'));
const PostsList = React.lazy(() => import('./pages/PostsList'));
const RequirementsRollupPage = React.lazy(() => import('./pages/RequirementsRollupPage'));
const DesignDocumentPage = React.lazy(() => import('./pages/DesignDocumentPage'));

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#5E35B1', // Deep Purple
      light: '#7E57C2',
      dark: '#4527A0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#D81B60', // Pink
      light: '#E91E63',
      dark: '#AD1457',
      contrastText: '#ffffff',
    },
    success: {
      main: '#10B981', // Emerald Green
      light: '#34D399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#F59E0B', // Amber
      light: '#FCD34D',
      dark: '#D97706',
      contrastText: '#ffffff',
    },
    info: {
      main: '#3B82F6', // Blue
      light: '#60A5FA',
      dark: '#2563EB',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // Lighter, cleaner background
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    grey: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    }
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h4: {
      fontWeight: 700,
      color: '#1e293b',
    },
    h5: {
      fontWeight: 600,
      color: '#334155',
    },
    h6: {
      fontWeight: 600,
      color: '#475569',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#334155',
    },
    subtitle2: {
      fontWeight: 500,
      color: '#64748b',
    },
    body1: {
      color: '#1e293b',
    },
    body2: {
      color: '#64748b',
    }
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          body: {
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            minHeight: '100vh',
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'inherit',
      },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiButton: {
        styleOverrides: {
            root: {
                borderRadius: 8,
                textTransform: 'none',
                padding: '8px 16px',
                fontWeight: 500,
                fontSize: '0.875rem',
                lineHeight: 1.75,
                letterSpacing: '0.02857em',
            },
            contained: {
                boxShadow: 'none',
                background: 'linear-gradient(135deg, #5E35B1 0%, #4527A0 100%)',
                '&:hover': {
                    background: 'linear-gradient(135deg, #4527A0 0%, #311B92 100%)',
                    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
                },
                '&:active': {
                    background: 'linear-gradient(135deg, #311B92 0%, #28159A 100%)',
                },
                '&.Mui-disabled': {
                    background: 'rgba(94, 53, 177, 0.3)',
                    color: 'rgba(255, 255, 255, 0.7)',
                    boxShadow: 'none',
                    '&:hover': {
                        background: 'rgba(94, 53, 177, 0.3)',
                        boxShadow: 'none',
                    }
                }
            },
            outlined: {
                borderColor: 'rgba(94, 53, 177, 0.5)',
                color: '#5E35B1',
                '&:hover': {
                    borderColor: '#5E35B1',
                    backgroundColor: 'rgba(94, 53, 177, 0.04)',
                },
                '&.Mui-disabled': {
                    borderColor: 'rgba(94, 53, 177, 0.2)',
                    color: 'rgba(94, 53, 177, 0.4)',
                    backgroundColor: 'transparent',
                }
            },
            text: {
                color: '#5E35B1',
                '&:hover': {
                    backgroundColor: 'rgba(94, 53, 177, 0.04)',
                },
                '&.Mui-disabled': {
                    color: 'rgba(94, 53, 177, 0.4)',
                    backgroundColor: 'transparent',
                }
            },
        },
    },
    MuiCard: {
        defaultProps: {
            elevation: 0,
        },
        styleOverrides: {
            root: {
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                transition: 'all 0.2s ease-in-out',
                background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                '&:hover': {
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-2px)',
                }
            }
        }
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                backgroundImage: 'none',
                background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                border: '1px solid #e2e8f0',
            }
        }
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: 8,
                fontWeight: 500,
            }
        }
    },
    MuiTooltip: {
        defaultProps: {
            arrow: true,
            enterDelay: 600,
            enterNextDelay: 300,
            leaveDelay: 0,
            disableInteractive: true,
        },
        styleOverrides: {
            popper: {
                pointerEvents: 'none',
            },
            tooltip: {
                backgroundColor: '#334155', // grey[700] — dark enough for white text
                color: '#ffffff',
                border: '1px solid #475569', // grey[600]
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.15), 0 2px 4px -1px rgba(0,0,0,0.1)',
                fontSize: '0.875rem',
                fontWeight: 400,
                borderRadius: 8,
                padding: '8px 12px',
                lineHeight: 1.5,
                pointerEvents: 'none',
                // Force all Typography children to inherit tooltip's white color
                // instead of keeping their theme-assigned gray values
                '& .MuiTypography-root': {
                    color: 'inherit',
                },
            },
            arrow: {
                color: '#334155',
                '&::before': {
                    border: '1px solid #475569',
                },
            },
        },
    },
    MuiContainer: {
        styleOverrides: {
            root: {
                '&.MuiContainer-maxWidthLg': {
                    maxWidth: '1200px',
                },
                '&.MuiContainer-maxWidthMd': {
                    maxWidth: '900px',
                }
            }
        }
    }
  }
});

// Page loading component for lazy loaded routes
const PageLoader = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="60vh"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress size={40} />
    <Typography variant="body2" color="text.secondary">
      Loading page...
    </Typography>
  </Box>
);

// Layout Component
const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const location = useLocation();

  // Initialize analytics and track page views
  useEffect(() => {
    if (user && currentOrganization) {
      analyticsService.initialize(user, currentOrganization);
    }
  }, [user, currentOrganization]);

  // Track page views for SPA navigation
  useEffect(() => {
    analyticsService.trackPageView(location.pathname, document.title);
  }, [location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {user && <NavBar handleDrawerToggle={handleDrawerToggle} />}
      {user && <Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, sm: 2, md: 3 }, 
          width: { sm: user ? 'calc(100% - 240px)' : '100%' },
          ml: { sm: user ? '0px' : 0 },
          mt: 8, // Add margin to account for the AppBar
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}
      >
        {children}
      </Box>
      {user && <ChatWrapper />}
    </Box>
  );
};

const RoadmapRedirect = () => {
  const { activeProject } = useProject();

  if (activeProject?.id) {
    return <Navigate to={`/roadmap/${activeProject.id}`} replace />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
      <Typography variant="h5" gutterBottom>
        Select a project to view its roadmap
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Choose a project from the sidebar projects list to access roadmap details.
      </Typography>
    </Box>
  );
};

function App() {
  // Initialize performance monitoring
  useEffect(() => {
    performanceService.initPerformanceMonitoring();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GoogleAnalytics />
      <AuthProvider>
        <AppDataProvider>
          <OrganizationProvider>
            <ProjectProvider>
              <FeedProvider>
                <PermissionsProvider>
                  <TutorialProvider>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/auth/callback" element={<CallbackPage />} />

            {/* Public invitation acceptance */}
            <Route path="/invite/:token" element={<InviteAcceptPage />} />

            {/* Public roadmap (no auth required) */}
            <Route path="/public/roadmap/:projectId" element={<PublicRoadmapPage />} />

            {/* Public survey response (no auth required) */}
            <Route path="/survey/:shareLink" element={<CustomerSurveyPage />} />

            {/* Public feature view (no auth required) */}
            <Route path="/public/features/:featureId" element={<PublicFeaturePage />} />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  {featureFlags.showHomePage ? <HomePage /> : <RoadmapRedirect />}
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Project Management Routes */}
            <Route path="/projects" element={
              <ProtectedRoute>
                <Layout>
                  <ProjectsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/projects/new" element={
              <ProtectedRoute>
                <Layout>
                  <ProjectCreationWithLimits>
                    <ProjectForm />
                  </ProjectCreationWithLimits>
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/projects/:id" element={<ProtectedRoute><Layout><ProjectDetailPage /></Layout></ProtectedRoute>} />

            {/* Project-specific routes */}
            <Route path="/projects/:projectId/features" element={<ProtectedRoute><Layout><FeaturesPage /></Layout></ProtectedRoute>} />
            <Route path="/projects/:projectId/features/new" element={<ProtectedRoute><Layout><FeatureForm /></Layout></ProtectedRoute>} />
            <Route path="/projects/:projectId/features/:featureId/edit" element={<ProtectedRoute><Layout><FeatureForm /></Layout></ProtectedRoute>} />
            <Route path="/projects/:projectId/features/:featureId" element={<ProtectedRoute><Layout><FeatureDetailPage /></Layout></ProtectedRoute>} />
            {featureFlags.showWorkboard && (
              <Route path="/projects/:projectId/workboard" element={<ProtectedRoute><Layout><Workboard /></Layout></ProtectedRoute>} />
            )}
            {featureFlags.showReleases && (
              <Route path="/projects/:projectId/releases" element={<ProtectedRoute><Layout><ReleasePlanner /></Layout></ProtectedRoute>} />
            )}
            
            {/* Requirements Routes */}
            <Route path="/projects/:projectId/requirements" element={<ProtectedRoute><Layout><RequirementsRollupPage /></Layout></ProtectedRoute>} />
            <Route path="/projects/:projectId/design-documents" element={<ProtectedRoute><Layout><DesignDocumentPage /></Layout></ProtectedRoute>} />
            
            {featureFlags.showActivityFeed && (
              <Route path="/feed" element={<ProtectedRoute><Layout><FeedPage /></Layout></ProtectedRoute>} />
            )}
            
            {/* Survey Routes */}
            <Route path="/surveys" element={
              <ProtectedRoute>
                <Layout>
                  <SurveysPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/surveys/create" element={
              <ProtectedRoute>
                <Layout>
                  <CreateInstantSurveyPage />
                </Layout>
              </ProtectedRoute>
            } />
            {/* More specific routes first */}
            <Route path="/surveys/:surveyId/responses" element={
              <ProtectedRoute>
                <Layout>
                  <DeployedSurveyResponsesPage />
                </Layout>
              </ProtectedRoute>
            } />
            {/* General survey routes */}
            <Route path="/surveys/:surveyId/edit" element={
              <ProtectedRoute>
                <Layout>
                  <EditSurveyPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/surveys/:surveyId/share" element={
              <ProtectedRoute>
                <Layout>
                  <SurveySharePage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/surveys/:surveyId/analytics" element={
              <ProtectedRoute>
                <Layout>
                  <SurveyAnalyticsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Survey Templates Routes */}
            <Route path="/survey-templates" element={
              <ProtectedRoute>
                <Layout>
                  <SurveyTemplatesPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/survey-templates/new" element={
              <ProtectedRoute>
                <Layout>
                  <TemplateEditorPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/survey-templates/:templateId/edit" element={
              <ProtectedRoute>
                <Layout>
                  <TemplateEditorPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/survey-templates/:templateId/preview" element={
              <ProtectedRoute>
                <Layout>
                  <TemplatePreviewPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/survey-templates/:templateId/deploy" element={
              <ProtectedRoute>
                <Layout>
                  <DeploySurveyPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Project Management Routes */}
            <Route path="/projects/edit/:id" element={
              <ProtectedRoute>
                <Layout>
                  <ProjectForm />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Roadmap Routes */}
            <Route path="/roadmap" element={
              <ProtectedRoute>
                <Layout>
                  <RoadmapRedirect />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/roadmap/:projectId" element={
              <ProtectedRoute>
                <Layout>
                  <PrivateRoadmapPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Organization Routes */}
            <Route path="/organization/settings" element={
              <ProtectedRoute>
                <Layout>
                  <OrganizationSettingsPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Account Routes */}
            <Route path="/account" element={
              <ProtectedRoute>
                <Layout>
                  <AccountPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Monitoring Routes (Admin Only) */}
            <Route path="/admin/monitoring" element={
              <ProtectedRoute>
                <Layout>
                  <MonitoringPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Project Members Routes */}
            <Route path="/projects/:projectId/members" element={
              <ProtectedRoute>
                <Layout>
                  <ProjectMembersPage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Billing Routes */}
            <Route path="/pricing" element={
              <Layout>
                <PricingPage />
              </Layout>
            } />
            <Route path="/plan-management" element={
              <Layout>
                <PlanManagementPage />
              </Layout>
            } />
            
            <Route path="/checkout/:planName" element={
              <ProtectedRoute>
                <Layout>
                  <CheckoutSummary />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/billing" element={
              <ProtectedRoute>
                <Layout>
                  <BillingSettings />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Social Media Routes */}
            <Route path="/social-media" element={
              <ProtectedRoute>
                <Layout>
                  <SocialMediaSettings />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/settings/atlassian" element={
              <ProtectedRoute>
                <Layout>
                  <AtlassianSettingsPage />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/social-media/compose" element={
              <ProtectedRoute>
                <Layout>
                  <PostComposer />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/social-media/posts" element={
              <ProtectedRoute>
                <Layout>
                  <PostsList />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Suspense>
                    <TutorialManager />
                  </TutorialProvider>
                </PermissionsProvider>
              </FeedProvider>
            </ProjectProvider>
          </OrganizationProvider>
        </AppDataProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;